import { supabase } from '../supabase';

// Types
export interface ClientPortalAccess {
  id: string;
  organization_id: string;
  client_id: string;
  username: string;
  is_active: boolean;
  permissions: ClientPortalPermissions;
  last_login_at?: string;
  login_attempts: number;
  locked_until?: string;
  created_at: string;
  client?: {
    id: string;
    business_name: string;
    email?: string;
    organization_id?: string;
  };
  projects?: ClientPortalProject[];
}

export interface ClientPortalProject {
  id: string;
  client_access_id: string;
  project_id: string;
  shared_sections?: string[] | null;
  project?: {
    id: string;
    name: string;
    description?: string;
    status?: string;
    metadata?: Record<string, unknown>;
  };
}

export interface ClientPortalSession {
  id: string;
  client_access_id: string;
  session_token: string;
  expires_at: string;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
}

export interface ClientPortalPermissions {
  view_tasks: boolean;
  request_tasks: boolean;
  comment_tasks: boolean;
  view_progress: boolean;
  view_documents: boolean;
  /** Tempo lavorato e monte ore. Spento di default: si accende accesso per accesso. */
  view_time: boolean;
}

export interface ClientAuthCredentials {
  username: string;
  password: string;
}

export interface ClientAuthResponse {
  success: boolean;
  error?: string;
  access?: ClientPortalAccess;
  session?: ClientPortalSession;
}

// I tipi dei dati letti dal portale vivono con i servizi del portale; qui restano esportati
// per non spezzare gli import esistenti.
export type {
  ClientEditableData,
  ClientBriefDocument,
  ClientNotification
} from './portal/types';

interface CreateClientAccessData {
  client_id: string;
  username: string;
  password: string;
  project_ids: string[];
  project_shared_sections?: Record<string, string[]>;
  permissions?: Partial<ClientPortalPermissions>;
  is_active?: boolean;
}

// Colonne leggibili dal browser. `password_hash` è escluso di proposito: la verifica della
// password avviene lato server con `portal_client_login`, e dalla fase 3 dei fix di sicurezza
// il ruolo anon non ha più il permesso di leggere quella colonna, quindi un `select('*')`
// fallirebbe con 42501. Vedi MD/security/SECURITY_AUDIT_2026-07-30_RLS.md
const ACCESS_COLUMNS = [
  'id', 'organization_id', 'client_id', 'username', 'is_active', 'permissions',
  'last_login_at', 'login_attempts', 'locked_until', 'created_at', 'updated_at',
  'created_by', 'notification_email', 'email_notification_preferences'
].join(', ');

// Segnaposto scritto alla creazione dell'accesso, prima che `portal_set_password` imposti
// quella vera. Non corrisponde a nessun formato riconosciuto dalla verifica lato server,
// quindi una riga rimasta con questo valore non permette di entrare.
const PASSWORD_PLACEHOLDER = '!non-impostata!';

// Durata della sessione, numero massimo di tentativi e durata del blocco ora vivono dentro
// `portal_client_login`: erano costanti di questo file quando il login girava nel browser.

const DEFAULT_PERMISSIONS: ClientPortalPermissions = {
  view_tasks: true,
  request_tasks: true,
  comment_tasks: true,
  view_progress: true,
  view_documents: false,
  // Spento: nessuno dei clienti già attivi deve trovarsi le ore sotto gli occhi da un giorno
  // all'altro. Si accende per singolo accesso da ClientPortalAccessManager.
  view_time: false
};

/**
 * Autenticazione del portale cliente e gestione degli accessi dal CRM.
 *
 * Le letture e le scritture fatte DAL portale non stanno più qui: passano dalle RPC di
 * sessione in `src/lib/services/portal/`. Quello che resta gira o come RPC di login, o con il
 * JWT di uno staff loggato nel CRM.
 */
export class ClientAuthService {
  /**
   * Create new client portal access (lato CRM, con JWT staff)
   */
  static async createClientAccess(data: CreateClientAccessData): Promise<ClientPortalAccess | null> {
    try {
      // Get current user and organization
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile) throw new Error('User profile not found');

      // Merge permissions with defaults
      const permissions = { ...DEFAULT_PERMISSIONS, ...data.permissions };

      // La password non viene mai cifrata nel browser. Si crea la riga con un segnaposto
      // (la colonna è NOT NULL) e subito dopo si imposta quella vera con
      // `portal_set_password`, che genera un hash bcrypt dentro il database.
      const { data: accessData, error } = await supabase
        .from('client_portal_access')
        .insert({
          organization_id: userProfile.organization_id,
          client_id: data.client_id,
          username: data.username,
          password_hash: PASSWORD_PLACEHOLDER,
          permissions,
          is_active: data.is_active !== false,
          created_by: userData.user.id
        })
        .select(`
          ${ACCESS_COLUMNS},
          client:clients(
            id, business_name, email
          )
        `)
        .single();

      if (error) throw error;

      const { error: passwordError } = await supabase.rpc('portal_set_password', {
        p_portal: 'client',
        p_access_id: accessData.id,
        p_password: data.password
      });

      if (passwordError) {
        // Senza una password valida l'accesso è inutilizzabile: si rimuove la riga invece
        // di lasciarla in giro con il segnaposto.
        await supabase.from('client_portal_access').delete().eq('id', accessData.id);
        throw passwordError;
      }

      // Add project associations
      if (data.project_ids.length > 0) {
        const projectInserts = data.project_ids.map(project_id => ({
          client_access_id: accessData.id,
          project_id,
          shared_sections: data.project_shared_sections?.[project_id] || null
        }));

        await supabase
          .from('client_portal_projects')
          .insert(projectInserts);
      }

      // accessData non contiene password_hash: non è fra le colonne selezionate
      return accessData;
    } catch (error) {
      console.error('Error creating client access:', error);
      return null;
    }
  }

  /**
   * Authenticate client and create session
   */
  static async authenticateClient(
    credentials: ClientAuthCredentials,
    ipAddress?: string,
    userAgent?: string
  ): Promise<ClientAuthResponse> {
    try {
      // Tutta l'autenticazione avviene dentro il database. La funzione controlla il blocco
      // dell'account, verifica la password, aggiorna i tentativi, crea la sessione e scrive
      // il log; restituisce l'accesso privo di `password_hash`. Il browser non vede mai
      // l'hash, e il ruolo anon non ha il permesso di riscriverlo.
      const { data, error } = await supabase.rpc('portal_client_login', {
        p_username: credentials.username,
        p_password: credentials.password,
        p_ip: ipAddress ?? null,
        p_user_agent: userAgent ?? null
      });

      if (error) throw error;
      if (!data?.success) {
        return { success: false, error: data?.error || 'Credenziali non valide' };
      }

      const access = data.access;

      // Normalize permissions
      if (typeof access.permissions === 'string') {
        try {
          access.permissions = JSON.parse(access.permissions);
        } catch {
          access.permissions = DEFAULT_PERMISSIONS;
        }
      }

      return {
        success: true,
        access,
        session: data.session
      };
    } catch (error) {
      console.error('Error authenticating client:', error);
      return { success: false, error: 'Autenticazione fallita' };
    }
  }

  /**
   * Validate client session
   */
  static async validateSession(sessionToken: string): Promise<ClientPortalAccess | null> {
    try {
      // La validazione avviene lato server: il browser non legge più la tabella delle
      // sessioni, quindi non può enumerare i token altrui. La funzione aggiorna anche
      // `last_accessed_at` e restituisce l'accesso con i progetti collegati.
      const { data, error } = await supabase.rpc('portal_client_validate_session', {
        p_token: sessionToken
      });

      if (error || !data) {
        return null;
      }

      // Normalize permissions
      if (typeof data.permissions === 'string') {
        try {
          data.permissions = JSON.parse(data.permissions);
        } catch {
          data.permissions = DEFAULT_PERMISSIONS;
        }
      }

      return data;
    } catch (error) {
      console.error('Error validating session:', error);
      return null;
    }
  }

  /**
   * Logout client (invalidate session)
   */
  static async logoutClient(sessionToken: string, _ipAddress?: string, _userAgent?: string): Promise<boolean> {
    try {
      // Cancellazione della sessione e scrittura del log fatte lato server, in un colpo solo.
      const { error } = await supabase.rpc('portal_client_logout', { p_token: sessionToken });
      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error logging out client:', error);
      return false;
    }
  }

  /**
   * Update client access settings (lato CRM, con JWT staff)
   */
  static async updateClientAccess(
    accessId: string,
    updates: {
      permissions?: Partial<ClientPortalPermissions>;
      is_active?: boolean;
      project_ids?: string[];
      project_shared_sections?: Record<string, string[]>;
    }
  ): Promise<boolean> {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) return false;

      // Update access record
      if (updates.permissions !== undefined || updates.is_active !== undefined) {
        const updateData: Record<string, unknown> = {};
        if (updates.permissions) updateData.permissions = updates.permissions;
        if (updates.is_active !== undefined) updateData.is_active = updates.is_active;

        const { error } = await supabase
          .from('client_portal_access')
          .update(updateData)
          .eq('id', accessId);

        if (error) throw error;
      }

      // Update project associations
      if (updates.project_ids !== undefined) {
        // Remove existing associations
        await supabase
          .from('client_portal_projects')
          .delete()
          .eq('client_access_id', accessId);

        // Add new associations
        if (updates.project_ids.length > 0) {
          const projectInserts = updates.project_ids.map(project_id => ({
            client_access_id: accessId,
            project_id,
            shared_sections: updates.project_shared_sections?.[project_id] || null
          }));

          await supabase
            .from('client_portal_projects')
            .insert(projectInserts);
        }
      }

      return true;
    } catch (error) {
      console.error('Error updating client access:', error);
      return false;
    }
  }

  /**
   * Toggle a section's portal visibility for all active accesses of a project
   */
  static async toggleSectionPortalAccess(projectId: string, sectionId: string): Promise<string[]> {
    try {
      // Get all active portal project records for this project
      const { data: portalProjects, error: fetchError } = await supabase
        .from('client_portal_projects')
        .select(`
          id,
          shared_sections,
          client_access:client_portal_access!inner(is_active)
        `)
        .eq('project_id', projectId);

      if (fetchError) throw fetchError;

      const activeRecords = (portalProjects || []).filter(
        (pp: any) => (pp.client_access as any)?.is_active
      );

      if (activeRecords.length === 0) return [];

      // Determine new state: if ANY record has this section, remove from all; otherwise add to all
      const anyHasSection = activeRecords.some(
        (pp: any) => pp.shared_sections && pp.shared_sections.includes(sectionId)
      );

      for (const record of activeRecords) {
        const current: string[] = record.shared_sections || [];
        const updated = anyHasSection
          ? current.filter((s: string) => s !== sectionId)
          : [...current, sectionId];

        await supabase
          .from('client_portal_projects')
          .update({ shared_sections: updated.length > 0 ? updated : null })
          .eq('id', record.id);
      }

      // Return the new aggregated shared sections
      const allSections = new Set<string>();
      for (const record of activeRecords) {
        const current: string[] = record.shared_sections || [];
        const updated = anyHasSection
          ? current.filter((s: string) => s !== sectionId)
          : [...current, sectionId];
        updated.forEach(s => allSections.add(s));
      }
      return Array.from(allSections);
    } catch (error) {
      console.error('Error toggling section portal access:', error);
      return [];
    }
  }

  /**
   * Reset client password
   */
  static async resetClientPassword(accessId: string, newPassword: string): Promise<boolean> {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) return false;

      // L'hash lo calcola il database (bcrypt). La funzione azzera anche i tentativi di
      // login, toglie l'eventuale blocco e cancella le sessioni aperte.
      const { error } = await supabase.rpc('portal_set_password', {
        p_portal: 'client',
        p_access_id: accessId,
        p_password: newPassword
      });

      return !error;
    } catch (error) {
      console.error('Error resetting client password:', error);
      return false;
    }
  }

  /**
   * Disable client access
   */
  static async disableClientAccess(accessId: string): Promise<boolean> {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) return false;

      const { error } = await supabase
        .from('client_portal_access')
        .update({ is_active: false })
        .eq('id', accessId);

      if (error) return false;

      // Invalidate all sessions
      await supabase
        .from('client_portal_sessions')
        .delete()
        .eq('client_access_id', accessId);

      return true;
    } catch (error) {
      console.error('Error disabling client access:', error);
      return false;
    }
  }

  /**
   * Get all client access records for a client
   */
  static async getClientAccessList(clientId?: string): Promise<ClientPortalAccess[]> {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) return [];

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile) return [];

      let query = supabase
        .from('client_portal_access')
        .select(`
          ${ACCESS_COLUMNS},
          client:clients(
            id, business_name, email
          ),
          projects:client_portal_projects(
            id,
            project_id,
            shared_sections,
            project:projects(id, name, status)
          )
        `)
        .eq('organization_id', userProfile.organization_id)
        .order('created_at', { ascending: false });

      if (clientId) {
        query = query.eq('client_id', clientId);
      }

      const { data, error } = await query;

      if (error) throw error;

      // password_hash non è fra le colonne selezionate
      return data || [];
    } catch (error) {
      console.error('Error fetching client access list:', error);
      return [];
    }
  }
}
